import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText, Check, X, Eye, Clock, Search, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UTRManagement = () => {
  const [utrSubmissions, setUtrSubmissions] = useState([]);
  const [selectedUtr, setSelectedUtr] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchUTRSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/utr-submissions`);
      setUtrSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching UTR submissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load UTR submissions',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchUTRSubmissions();
  }, []);

  const handleVerify = async (utr, status) => {
    setLoading(true);
    try {
      // Use the id field returned from backend
      const utrId = utr.id || utr._id;
      await axios.put(`${API}/admin/utr-submissions/${utrId}/verify`, { status });
      toast({ 
        title: '✅ Success', 
        description: `UTR ${status === 'Approved' ? 'approved' : 'rejected'} successfully` 
      });
      fetchUTRSubmissions();
    } catch (error) {
      console.error('UTR verification error:', error);
      toast({ 
        title: '❌ Error', 
        description: error.response?.data?.detail || error.response?.data?.error || 'Verification failed. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (utr) => {
    setSelectedUtr(utr);
    setPreviewOpen(true);
  };

  const handleDelete = async (utr) => {
    if (!window.confirm(`Are you sure you want to delete UTR submission for ${utr.name}? This action cannot be undone.`)) {
      return;
    }
    setLoading(true);
    try {
      const utrId = utr.id || utr._id;
      await axios.delete(`${API}/admin/utr-submissions/${utrId}`);
      toast({ 
        title: '✅ Deleted', 
        description: 'UTR submission deleted successfully' 
      });
      fetchUTRSubmissions();
    } catch (error) {
      console.error('UTR delete error:', error);
      toast({ 
        title: '❌ Error', 
        description: error.response?.data?.detail || 'Delete failed. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Successful':
      case 'Approved':
        return 'from-green-500 to-emerald-500';
      case 'Not Issued':
      case 'Rejected':
        return 'from-red-500 to-rose-500';
      default:
        return 'from-yellow-500 to-blue-500';
    }
  };

  // Filter UTR submissions by search term
  const filteredSubmissions = utrSubmissions.filter(utr => {
    const search = searchTerm.toLowerCase();
    return (
      utr.paymentId?.toLowerCase().includes(search) ||
      utr.name?.toLowerCase().includes(search) ||
      utr.email?.toLowerCase().includes(search) ||
      utr.phone?.includes(search) ||
      utr.clientId?.toString().includes(search) ||
      utr.progressId?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">UTR Submissions</h2>
        <div className="text-sm text-slate-600">Total: {utrSubmissions.length}</div>
      </div>

      {/* Search Bar */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Payment ID, Name, Email, Phone, Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* UTR Submissions List */}
      <div className="grid gap-4">
        {filteredSubmissions.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-600">No UTR submissions found</p>
            </CardContent>
          </Card>
        ) : (
          filteredSubmissions.map((utr) => (
            <Card key={utr.id || utr.paymentId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all group overflow-hidden animate-in fade-in slide-in-from-bottom-2">
              <div className={`absolute inset-0 bg-gradient-to-r ${getStatusColor(utr.status)} opacity-0 group-hover:opacity-5 transition-opacity`} />
              <CardContent className="pt-6 relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Payment ID</p>
                      <p className="font-semibold text-blue-400">{utr.paymentId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Client</p>
                      <p className="text-slate-800 font-medium">#{utr.clientId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Plan</p>
                      <p className="text-slate-800">{utr.planName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Amount</p>
                      <p className="text-green-600 font-bold">₹{utr.planPrice}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Name</p>
                      <p className="text-slate-800">{utr.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Email</p>
                      <p className="text-slate-800 text-xs">{utr.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Phone</p>
                      <p className="text-slate-800">{utr.phone}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Status</p>
                      <span className={`inline-block px-2 py-1 rounded text-xs bg-gradient-to-r ${getStatusColor(utr.status)} text-slate-800 font-medium`}>
                        {utr.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button size="sm" variant="ghost" onClick={() => handlePreview(utr)} className="hover:bg-slate-100">
                      <Eye className="w-4 h-4" />
                    </Button>
                    {utr.status === 'Pending' && (
                      <>
                        <Button 
                          size="sm" 
                          onClick={() => handleVerify(utr, 'Approved')}
                          disabled={loading}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleVerify(utr, 'Rejected')}
                          disabled={loading}
                          className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 shadow-lg hover:shadow-red-500/50 transition-all hover:scale-105"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button 
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(utr)}
                      disabled={loading}
                      className="hover:bg-red-500/20 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Submitted: {new Date(utr.createdAt).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-blue-400 text-xl">Payment Screenshot Preview</DialogTitle>
          </DialogHeader>
          {selectedUtr && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-slate-200">
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Payment ID</p>
                  <p className="font-semibold text-slate-800">{selectedUtr.paymentId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Progress ID</p>
                  <p className="font-semibold text-slate-800">{selectedUtr.progressId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Plan</p>
                  <p className="text-slate-800">{selectedUtr.planName} - ₹{selectedUtr.planPrice}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs bg-gradient-to-r ${getStatusColor(selectedUtr.status)} text-slate-800 font-medium`}>
                    {selectedUtr.status}
                  </span>
                </div>
              </div>
              {selectedUtr.screenshotPath ? (
                <div className="border border-slate-200 rounded-lg p-2 bg-white">
                  <img 
                    src={selectedUtr.screenshotPath.startsWith('http') 
                      ? selectedUtr.screenshotPath 
                      : `${BACKEND_URL}${selectedUtr.screenshotPath}`
                    } 
                    alt="Payment Screenshot" 
                    className="w-full h-auto rounded max-h-96 object-contain"
                    onError={(e) => {
                      console.error('Screenshot load error:', selectedUtr.screenshotPath);
                      e.target.src = 'https://via.placeholder.com/800x600?text=Screenshot+Not+Available';
                    }}
                  />
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg p-8 bg-white text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-600" />
                  <p className="text-slate-500">No screenshot available</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UTRManagement;
